import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/login.css";
import leftImage from "../assets/landing1.png";
import InstructionsModal from "../components/InstructionsModal";
import { postJson } from "../network/api";
import { ApiEndpoints } from "../network/config";

// ✅ Lottie imports
import Lottie from "lottie-react";
import doneAnimation from "../assets/Done.json";
import alreadyAnimation from "../assets/Already.json";

const Login = () => {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");

  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(""); // success | error
  const [popupTitle, setPopupTitle] = useState("");
  const [popupSubtitle, setPopupSubtitle] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Instructions modal
  const [showInstructions, setShowInstructions] = useState(false);

  // 🔁 WhatsApp animation states
  const [waLang, setWaLang] = useState("kn"); // kn | en
  const [waFade, setWaFade] = useState(true);

  const navigate = useNavigate();
  const closeTimerRef = useRef(null);

  // ---------------------------------------------------------
  // Auto-close popup + redirect on success
  // ---------------------------------------------------------
  useEffect(() => {
    if (showPopup) {
      closeTimerRef.current = setTimeout(() => {
        setShowPopup(false);
        if (popupType === "success") {
          navigate("/landingpage");
        }
      }, 2000);
    }
    return () => clearTimeout(closeTimerRef.current);
  }, [showPopup, popupType, navigate]);

  // ---------------------------------------------------------
  // WhatsApp instruction animation loop
  // ---------------------------------------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      setWaFade(false);

      setTimeout(() => {
        setWaLang((prev) => (prev === "kn" ? "en" : "kn"));
        setWaFade(true);
      }, 500);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // ---------------------------------------------------------
  // Popup helper
  // ---------------------------------------------------------
  const show = (type, title, subtitle) => {
    setPopupType(type);
    setPopupTitle(title);
    setPopupSubtitle(subtitle);
    setShowPopup(true);
  };

  // ---------------------------------------------------------
  // Mobile number validation (digits only)
  // ---------------------------------------------------------
  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) setMobile(value);
  };

  // ---------------------------------------------------------
  // Login Handler
  // ---------------------------------------------------------
  const handleLogin = async (e) => {
    e.preventDefault();

    if (mobile.length !== 10) {
      return show("error", "Invalid Mobile Number", "Please enter 10 digits.");
    }

    if (!password.trim()) {
      return show("error", "Password Required", "Please enter your password.");
    }

    try {
      const res = await postJson(ApiEndpoints.login, {
        mobileNumber: mobile,
        password: password,
      });

      if (res?.data) {
        sessionStorage.setItem("userId", res.data.id);
        sessionStorage.setItem("userName", res.data.name);
        sessionStorage.setItem("userMobile", res.data.mobileNumber);

        show("success", "Login Successful!", "Redirecting...");
        setTimeout(() => navigate("/landingpage"), 1500);
        return;
      }

      show("error", "Login Failed", res?.message || "Please try again.");
    } catch (err) {
      let message = "Login failed. Please try again.";

      try {
        const body = JSON.parse(err.body);
        if (body?.error?.message) {
          message = body.error.message;
        }
      } catch {}

      show("error", "Login Error", message);
    }
  };

  return (
    <div className="login-page">
      {/* ================= POPUP ================= */}
      {showPopup && (
        <div className="popup-message" role="alert">
          <div className="popup-card-content">
            <div className="popup-animation">
              <Lottie
                animationData={
                  popupType === "success"
                    ? doneAnimation
                    : alreadyAnimation
                }
                loop={false}
                style={{ width: 90, height: 90 }}
              />
            </div>

            <div className="popup-text">
              <p className="popup-title">{popupTitle}</p>
              <p className="popup-subtitle">{popupSubtitle}</p>
            </div>
          </div>
        </div>
      )}

      {/* ================= LEFT IMAGE ================= */}
      <div className="left-side">
        <img src={leftImage} alt="Lingayath Weds" className="left-image" />
      </div>

      {/* ================= LOGIN FORM ================= */}
      <div className="right-side">
        <div className="login-card">
          <h1 className="title">Welcome to Lingayath Weds</h1>

          <button
            type="button"
            className="instructions-btn"
            onClick={() => setShowInstructions(true)}
          >
            Login Instructions
          </button>

          <form onSubmit={handleLogin}>
            <label>Mobile Number*</label>
            <input
              type="text"
              placeholder="Enter Mobile Number"
              value={mobile}
              onChange={handleMobileChange}
              maxLength="10"
            />

            <label>Password*</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <span
                className="eye-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </span>
            </div>

            <button type="submit" className="login-btn">
              Login
            </button>

            <p className="forgot">
              <Link to="/forgotpassword" className="forgot-link">
                Forgot Password?
              </Link>
            </p>

            {/* 🔁 Animated WhatsApp Instructions */}
            <div
              className={`whatsapp-instructions ${
                waFade ? "wa-fade-in" : "wa-fade-out"
              }`}
            >
              {waLang === "en" ? (
                <ul className="instructions-list">
                  <li>
                    To receive important updates and notifications via WhatsApp,
                    please send <strong>“Hi”</strong> from your registered mobile
                    number to <strong>+91 63648-98660</strong>.
                  </li>
                  <li>
                    If you are unsure about your registered mobile number, please
                    contact your branch administrator for assistance.
                  </li>
                </ul>
              ) : (
                <ul className="instructions-list kannada-text">
                  <li>
                    ವಾಟ್ಸಾಪ್ ಮೂಲಕ ಪ್ರಮುಖ ಮಾಹಿತಿಗಳು ಮತ್ತು ಅಪ್‌ಡೇಟ್‌ಗಳನ್ನು ಪಡೆಯಲು,
                    ದಯವಿಟ್ಟು ನಿಮ್ಮ ನೋಂದಾಯಿತ ಮೊಬೈಲ್ ಸಂಖ್ಯೆಯಿಂದ{" "}
                    <strong>+91 63648-98660</strong> ಈ ಸಂಖ್ಯೆಗೆ{" "}
                    <strong>“Hi”</strong> ಎಂದು ಸಂದೇಶವನ್ನು ಕಳುಹಿಸಿ.
                  </li>
                  <li>
                    ನಿಮ್ಮ ನೋಂದಾಯಿತ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ತಿಳಿಯದಿದ್ದರೆ, ದಯವಿಟ್ಟು ನಿಮ್ಮ
                    ಶಾಖೆಯನ್ನು ಸಂಪರ್ಕಿಸಿ.
                  </li>
                </ul>
              )}
            </div>

            {/* ✅ WhatsApp Send Message Button (Extracted & Added) */}
            <a
              href="https://wa.me/916364898660?text=Hi"
              target="_blank"
              rel="noopener noreferrer"
              className="send-message-btn"
            >
              Send Message
            </a>
          </form>
        </div>
      </div>

      {/* ================= INSTRUCTIONS MODAL ================= */}
      <InstructionsModal
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
      />
    </div>
  );
};

export default Login;
